/**
 * /account — customer account page (spec §4.1, design §4.3 — Phase 2).
 *
 * Grown from the Phase 1 stub (review F3): account status badge + explainer,
 * app download block, current plan (derived from the latest PAID order's
 * offering line-item snapshot — accurate, unlike the signup intent), orders &
 * purchase history table, "Add products" teaser, and the support block
 * (support / FAQ / account deletion links to the marketing site).
 */
import { Link } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { ExternalLink, Loader2, LogOut, ShoppingBag, Smartphone } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useAuthStore } from '@/store/auth-store';
import { useFunnelState, useOfferings } from '@/hooks/useFunnel';
import { useMyOrders } from '@/hooks/useMyOrders';
import { ACCOUNT_STATUS_LABELS, MARKETING_SITE_URL, SUPPORT_URL, formatPrice } from '@/lib/funnel';
import type { AccountStatus, FunnelOrderStatus, MyOrder } from '@/types';

// Manual approval reinstated (ClickUp 86baab6dk): payment moves the account
// to 'pending' until the team prepares it and assigns the story persona.
const ACCOUNT_STATUS_DESCRIPTIONS: Record<AccountStatus, string> = {
  pending:
    'Our team is preparing your account and assigning your story persona. ' +
    'We will notify you when your app access is ready.',
  approved: 'Your account is ready — download the app to begin collecting your stories.',
  active: 'Your account is active. Keep collecting your stories in the app.',
  completed: 'Your story collection period is complete. Thank you for trusting us.',
};

const ORDER_STATUS_LABELS: Record<FunnelOrderStatus, string> = {
  pending_payment: 'Pending payment',
  paid: 'Paid',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

const ORDER_STATUS_BADGE_VARIANTS: Record<
  FunnelOrderStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  pending_payment: 'secondary',
  paid: 'default',
  cancelled: 'outline',
  refunded: 'destructive',
};

/** Plan name from the newest PAID order's offering line-item snapshot. */
function paidPlanName(orders: MyOrder[] | undefined): string | null {
  const paidOrder = orders?.find((order) => order.status === 'paid');
  return paidOrder?.items.find((item) => item.itemType === 'offering')?.nameSnapshot ?? null;
}

export function AccountStubPage() {
  useDocumentTitle('My Account · LegacyOrb');
  const user = useAuthStore((state) => state.user);
  const { signOut } = useClerk();
  const { data: funnelState, isLoading } = useFunnelState();
  const { data: offerings } = useOfferings();
  const { data: orders, isLoading: ordersLoading, isError: ordersError } = useMyOrders();

  // Sign out, then leave the authenticated app for the public marketing site
  // (legacyorb.com / dev-marketing.legacyorb.com) where "My Account" lets the
  // user sign back in. A hard navigation guarantees the cross-origin redirect.
  const handleSignOut = async () => {
    await signOut();
    window.location.href = MARKETING_SITE_URL;
  };

  // Current plan — prefer the offering nameSnapshot from the latest paid
  // order (accurate; review F9). Only when the user has NO orders at all do
  // we fall back to the signup intent's preferred slug, clearly labelled.
  const planFromOrders = paidPlanName(orders);
  const intentPlanName =
    orders && orders.length === 0 && funnelState?.intent?.preferredOfferingSlug
      ? (offerings?.find((o) => o.slug === funnelState.intent?.preferredOfferingSlug)?.name ?? null)
      : null;

  return (
    // Brand chrome (header/footer/theme) comes from MarketingChromeLayout in
    // App.tsx — this page only renders its own centered content column.
    <div className="px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-3xl">
        <div className="mb-8 flex flex-col items-center text-center">
          <h1 className="font-display text-3xl font-bold tracking-tight text-foreground">
            Your Account
          </h1>
          {user && (
            <p className="mt-2 text-muted-foreground">
              Welcome{user.firstName ? `, ${user.firstName}` : ''}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* (a) Account status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-lg">
                  Account status
                  <Badge
                    variant={funnelState?.accountStatus === 'active' ? 'default' : 'secondary'}
                  >
                    {funnelState ? ACCOUNT_STATUS_LABELS[funnelState.accountStatus] : 'Unknown'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {funnelState
                    ? ACCOUNT_STATUS_DESCRIPTIONS[funnelState.accountStatus]
                    : 'We could not load your account status. Please try again later.'}
                </p>
              </CardContent>
            </Card>

            {/* (b) App download — while the account is pending approval the
                page must not contradict the status card above by pushing a
                download (ClickUp 86baab6dk), so the CTA is swapped for the
                what-happens-next explainer until the team unlocks access. */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Smartphone className="h-5 w-5 text-muted-foreground" />
                  Download the app
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {funnelState?.accountStatus === 'pending' ? (
                  <p className="text-sm text-muted-foreground">
                    The LegacyOrb mobile app is where your stories are collected. App access is
                    unlocked once our team finishes preparing your account — we&apos;ll email you
                    the moment it&apos;s ready.
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      The LegacyOrb mobile app is where your stories are collected. Sign in with
                      your account e-mail to get started.
                    </p>
                    <Button asChild variant="outline">
                      <a
                        href={`${MARKETING_SITE_URL}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Get the app
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* (c) Current plan */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your plan</CardTitle>
              </CardHeader>
              <CardContent>
                {ordersLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : planFromOrders ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Current plan</span>
                      <span className="font-medium text-foreground">{planFromOrders}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Story usage</span>
                      <span className="text-muted-foreground">Coming soon</span>
                    </div>
                  </div>
                ) : intentPlanName ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Plan selected at signup</span>
                      <span className="font-medium text-foreground">{intentPlanName}</span>
                    </div>
                    <p className="text-muted-foreground">You have not completed a purchase yet.</p>
                    <Button asChild className="mt-2">
                      <Link to="/start">Start your Legacy</Link>
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      {ordersError
                        ? 'We could not load your plan right now. Please try again later.'
                        : 'You do not have an active plan yet.'}
                    </p>
                    {!ordersError && (
                      <Button asChild>
                        <Link to="/start">Start your Legacy</Link>
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* (d) Orders & purchase history */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Orders &amp; purchase history</CardTitle>
              </CardHeader>
              <CardContent>
                {ordersLoading ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : ordersError ? (
                  <p className="text-sm text-muted-foreground">
                    We could not load your order history. Please try again later.
                  </p>
                ) : !orders || orders.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No orders yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell className="whitespace-nowrap">
                            {format(new Date(order.createdAt), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell>
                            <ul className="space-y-0.5">
                              {order.items.map((item, index) => (
                                <li key={`${order.id}-${index}`} className="text-sm">
                                  {item.nameSnapshot}
                                  {item.quantity > 1 ? ` x${item.quantity}` : ''}
                                </li>
                              ))}
                            </ul>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right font-medium">
                            {formatPrice(order.totalCents)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={ORDER_STATUS_BADGE_VARIANTS[order.status]}>
                              {ORDER_STATUS_LABELS[order.status]}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* (e) Add products — post-purchase checkout is deferred (design
                §4.3 /account/add-products); until it ships, point users at
                the marketing catalog. */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                  Add products
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Extra story packs, the physical orb, Legacy Book and Vault will be purchasable
                  here soon.
                </p>
                <Button asChild variant="outline">
                  <a
                    href={`${MARKETING_SITE_URL}/products`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Explore products
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* (f) Support */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Support</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a
                      href={SUPPORT_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground underline-offset-4 hover:underline"
                    >
                      Submit a support request
                    </a>
                  </li>
                  <li>
                    {/* FAQ page doesn't exist yet — placeholder until it ships. */}
                    <span className="text-muted-foreground">FAQ — coming soon</span>
                  </li>
                  <li>
                    <a
                      href={`${MARKETING_SITE_URL}/account-deletion`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground underline-offset-4 hover:underline"
                    >
                      Request account deletion
                    </a>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* (g) Sign out */}
            <div className="flex justify-center pt-2">
              <Button variant="outline" onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
